import { useEffect } from "react";

/**
 * ScrollFocusEngine — global, GPU-only "viewport-center focus" layer.
 *
 * For every element on the page carrying `data-focus`, computes a 0→1
 * proximity value to the viewport vertical center and writes it as the
 * CSS custom property `--focus` on that element. The visual response
 * (subtle scale + lift + opacity) lives entirely in index.css so this
 * file stays cheap and the effect composes safely with any existing
 * transform/animation on the element.
 *
 * Hard rules:
 *  - Pure progressive enhancement. Removing this component leaves the
 *    site visually intact.
 *  - Respects prefers-reduced-motion (no work scheduled).
 *  - rAF-throttled, single passive scroll/resize listener.
 *  - No DOM mutation other than CSS variable writes.
 *  - Skips low-end devices (saveData / few cores / low memory).
 */
const ScrollFocusEngine = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reduce.matches) return;

    const nav = navigator as any;
    const lowMem = typeof nav.deviceMemory === "number" && nav.deviceMemory <= 2;
    const lowCpu = typeof nav.hardwareConcurrency === "number" && nav.hardwareConcurrency <= 2;
    const saveData = nav.connection && nav.connection.saveData === true;
    if (lowMem || lowCpu || saveData) return;

    let raf = 0;
    let elements: HTMLElement[] = [];
    let pickers: HTMLElement[] = [];

    const collect = () => {
      elements = Array.from(document.querySelectorAll<HTMLElement>("[data-focus]"));
      pickers = Array.from(document.querySelectorAll<HTMLElement>("[data-picker]"));
    };

    const updateList = (
      list: HTMLElement[],
      falloffRatio: number,
      writeDir: boolean,
      centerFor?: (el: HTMLElement) => number | null,
    ) => {
      const vh = window.innerHeight || 1;
      for (let i = 0; i < list.length; i++) {
        const el = list[i];
        if (!el.isConnected) continue;
        const rect = el.getBoundingClientRect();
        const center = centerFor ? (centerFor(el) ?? vh / 2) : vh / 2;
        const falloff = vh * falloffRatio;
        if (rect.bottom < -200 || rect.top > vh + 200) {
          if (el.style.getPropertyValue("--focus") !== "0") {
            el.style.setProperty("--focus", "0");
          }
          continue;
        }
        const elCenter = rect.top + rect.height / 2;
        const dist = Math.abs(elCenter - center);
        const raw = Math.max(0, 1 - dist / falloff);
        const eased = raw * raw * (3 - 2 * raw);
        el.style.setProperty("--focus", eased.toFixed(3));
        if (writeDir) {
          el.style.setProperty("--picker-dir", elCenter < center ? "1" : "-1");
        }
      }
    };

    const update = () => {
      raf = 0;
      updateList(elements, 0.5, false);
      updateList(pickers, 0.32, true, (el) => {
        const scroller = el.closest<HTMLElement>("[data-picker-scroller]");
        if (!scroller) return null;
        const r = scroller.getBoundingClientRect();
        return r.top + r.height / 2;
      });
    };

    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    collect();
    schedule();

    const mo = new MutationObserver(() => {
      collect();
      schedule();
    });
    mo.observe(document.body, { childList: true, subtree: true });

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    // Capture-phase listener picks up scroll events from any nested
    // scroll container (e.g. the burger menu's internal scroll body).
    // Scroll events don't bubble, so the capture phase is required.
    document.addEventListener("scroll", schedule, { passive: true, capture: true });

    return () => {
      mo.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      document.removeEventListener("scroll", schedule, { capture: true } as any);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return null;
};

export default ScrollFocusEngine;
